import {
  Box,
  Checkbox,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import OpenRunsRow from "./row";
import { getRowId, toRunDateQuery } from "./utils";
import {
  STEP_OPTIONS,
  INITIAL_SEARCH_CRITERIA,
  INITIAL_POST_DATA,
} from "./constants";
import { base64ToExcel } from "Pages/DynamicGrid/bulkExportActions";

import {
  ExportBatchesExcel,
  GetAllBatches,
} from "Services/Oncology/oncologyService";
import SearchDateRangePicker from "Shared/Common/DatePicker/SearchDateRangePicker";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent, {
  AnyPermission,
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import { sortById, SortingTypeI } from "Utils/consts";
import { StyledDropButton, StyledDropMenu } from "Utils/Style/Dropdownstyle";
import { reactSelectSMStyle, styles } from "Utils/Common";

const SORTABLE_COLUMNS = [
  { key: "batchName", label: "Batch Name", width: "160px" },
  { key: "batchCode", label: "Batch #", width: "120px" },
  { key: "runDate", label: "Run Date", width: "150px" },
  { key: "plateBarcodeNo", label: "Plate Barcode No", width: "140px" },
];

const FILTER_FIELD_LABELS: Record<keyof typeof INITIAL_SEARCH_CRITERIA, string> =
  {
    batchName: "Batch Name",
    batchCode: "Batch #",
    runDate: "Run Date",
    plateBarcodeNo: "Plate Barcode No",
    stepNumber: "Step #",
  };

/** Renders toolbar controls for search, add, export, and reset actions. */
const OpenRunsToolbar: React.FC<any> = ({
  t,
  pageSize,
  setPageSize,
  setCurPage,
  isBtnDisabled,
  loading,
  handleAddNewBatch,
  anchorEl,
  setAnchorEl,
  handleSearch,
  handleReset,
  handleExportAll,
  handleExportSelected,
}) => (
  <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
    <div className="d-flex align-items-center responsive-flexed-actions gap-2">
      <div className="d-flex align-items-center">
        <span className="fw-400 mr-2">{t("Records")}</span>
        <select
          id="OpenRunsRecords"
          className="form-select w-100px h-33px rounded py-2"
          value={pageSize}
          onChange={(e) => {
            setPageSize(parseInt(e.target.value));
            setCurPage(1);
          }}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
      <PermissionComponent
        moduleName="Oncology LIS"
        pageName="View Runs"
        permissionIdentifier="AddNew"
      >
        {({ pageName, moduleName, permissionIdentifier }) => (
          <button
            id="OpenRunsAddNew"
            className="btn btn-success btn-sm btn-primary--icon px-7"
            disabled={isBtnDisabled || loading}
            onClick={handleAddNewBatch}
          >
            <i className="fa-solid fa-plus me-1" />
            <span style={{ fontSize: 11 }}>
              {getPermissionDisplayName(
                pageName,
                moduleName,
                permissionIdentifier,
                "Create Batch"
              )}
            </span>
          </button>
        )}
      </PermissionComponent>
      <div className="border-0 d-flex justify-content-sm-start">
        <div className="d-flex gap-1 gap-lg-1">
          <AnyPermission
            moduleName="Facility"
            pageName="Manage Facility"
            permissionIdentifiers={[
              "ExportAllRecords",
              "ExportSelectedRecords",
            ]}
          >
            <StyledDropButton
              id="OpenRunsExportButton"
              aria-haspopup="true"
              aria-expanded={anchorEl.dropdown2 ? "true" : undefined}
              onClick={(e: any) =>
                setAnchorEl((prev: any) => ({
                  ...prev,
                  dropdown2: e.currentTarget,
                }))
              }
              className="btn btn-excle btn-sm"
            >
              <i
                style={{ color: "white", fontSize: "20px", paddingLeft: "2px" }}
                className="fa"
              >
                &#xf1c3;
              </i>
              <span className="svg-icon svg-icon-5 m-0">
                <ArrowBottomIcon />
              </span>
            </StyledDropButton>
            <StyledDropMenu
              anchorEl={anchorEl.dropdown2}
              open={Boolean(anchorEl.dropdown2)}
              onClose={() =>
                setAnchorEl((prev: any) => ({ ...prev, dropdown2: null }))
              }
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <PermissionComponent
                moduleName="Oncology LIS"
                pageName="View Run"
                permissionIdentifier="ExportAllRecords"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <MenuItem
                    id="OpenRuns_ExportAllRecords"
                    onClick={() => {
                      setAnchorEl((prev: any) => ({
                        ...prev,
                        dropdown2: null,
                      }));
                      handleExportAll();
                    }}
                  >
                    {getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      "Export All Records"
                    )}
                  </MenuItem>
                )}
              </PermissionComponent>
              <PermissionComponent
                moduleName="Oncology LIS"
                pageName="View Run"
                permissionIdentifier="ExportSelectedRecords"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <MenuItem
                    id="OpenRuns_ExportSelectedRecords"
                    onClick={() => {
                      setAnchorEl((prev: any) => ({
                        ...prev,
                        dropdown2: null,
                      }));
                      handleExportSelected();
                    }}
                  >
                    {getPermissionDisplayName(
                      pageName,
                      moduleName,
                      permissionIdentifier,
                      "Export Selected Records"
                    )}
                  </MenuItem>
                )}
              </PermissionComponent>
            </StyledDropMenu>
          </AnyPermission>
        </div>
      </div>
    </div>
    <div className="d-flex align-items-center gap-2">
      <button
        id="OpenRunsSearch"
        className="btn btn-linkedin btn-sm fw-500"
        onClick={handleSearch}
      >
        {t("Search")}
      </button>
      <button
        id="OpenRunsReset"
        className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
        onClick={handleReset}
      >
        <span>{t("Reset")}</span>
      </button>
    </div>
  </div>
);

/** Renders pagination summary and controls for the open runs list. */
const OpenRunsPagination: React.FC<any> = ({
  t,
  pageSize,
  curPage,
  total,
  pageNumbers,
  showPage,
  prevPage,
  nextPage,
  totalPages,
}) => (
  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
    <p className="pagination-total-record mb-0">
      <span>
        {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
        {Math.min(pageSize * curPage, total)} {t("of Total")}
        <span> {total} </span>
        {t("entries")}
      </span>
    </p>
    <ul className="d-flex align-items-center justify-content-end custome-pagination p-0 mb-0">
      <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
        <i className="fa fa-angle-double-left" />
      </li>
      <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
        <i className="fa fa-angle-left" />
      </li>
      {pageNumbers.map((page: number) => (
        <li
          key={page}
          className={`px-2 ${page === curPage ? "font-weight-bold bg-primary text-white h-33px" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => showPage(page)}
        >
          {page}
        </li>
      ))}
      <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
        <i className="fa fa-angle-right" />
      </li>
      <li
        className="btn btn-lg p-2 h-33px"
        onClick={() => showPage(totalPages === 0 ? curPage : totalPages)}
      >
        <i className="fa fa-angle-double-right" />
      </li>
    </ul>
  </div>
);

const OpenRuns: React.FC = () => {
  const { t } = useLang();
  const labTimeZone = useSelector(
    (state: any) => state?.Reducer?.selectedTenantInfo?.labTimeZone
  );

  const [triggerSearch, setTriggerSearch] = useState(false);
  const [sortTrigger, setSortTrigger] = useState(false);
  const [reset, setReset] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(INITIAL_SEARCH_CRITERIA);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);

  const [expandedId, setExpandedId] = useState<string | number | null>(null);
  const [selected, setSelected] = useState<Record<string | number, boolean>>(
    {}
  );
  const [localRows, setLocalRows] = useState<any[]>([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);

  const [anchorEl, setAnchorEl] = useState({
    dropdown2: null as HTMLElement | null,
  });

  const pagination = usePagination();
  const {
    curPage,
    pageSize,
    setCurPage,
    setPageSize,
    setTotal,
    total,
    totalPages,
    pageNumbers,
    showPage,
    prevPage,
    nextPage,
  } = pagination;

  const handleSort = (col: string) => {
    const newOrder =
      sort.clickedIconData === col
        ? sort.sortingOrder === "asc"
          ? "desc"
          : "asc"
        : "asc";
    setSorting({ sortingOrder: newOrder, clickedIconData: col });
    setSortTrigger((p) => !p);
  };

  const sortIcons = (col: string) => ({
    up: `${sort.sortingOrder === "desc" && sort.clickedIconData === col ? "text-success fs-7" : "text-gray-700 fs-7"} p-0 m-0`,
    down: `${sort.sortingOrder === "asc" && sort.clickedIconData === col ? "text-success fs-7" : "text-gray-700 fs-7"} p-0 m-0`,
  });

  const batchQueryModel = useMemo(
    () => ({
      batchName: searchCriteria.batchName || null,
      batchCode: searchCriteria.batchCode || null,
      runDate: searchCriteria.runDate
        ? toRunDateQuery(searchCriteria.runDate, labTimeZone)
        : null,
      plateBarcodeNo: searchCriteria.plateBarcodeNo || null,
      stepNumber: searchCriteria.stepNumber || null,
      batchStatus: "open",
    }),
    [searchCriteria, labTimeZone]
  );

  const queryPayload = useMemo(
    () => ({
      pageNumber: curPage,
      pageSize,
      queryModel: batchQueryModel,
      sortColumn: sort.clickedIconData || "Id",
      sortDirection: sort.sortingOrder || "Desc",
    }),
    [curPage, pageSize, batchQueryModel, sort.clickedIconData, sort.sortingOrder]
  );

  // Data Fetching
  const {
    data: queryResult,
    isFetching: loading,
    refetch,
  } = useQuery(
    ["view-runs-open", curPage, pageSize, triggerSearch, sortTrigger, reset],
    () => GetAllBatches(queryPayload),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (resp: any) => {
        const count =
          resp?.data?.total ??
          resp?.data?.totalCount ??
          resp?.data?.data?.length ??
          0;
        setTotal(typeof count === "number" ? count : 0);
      },
      onError: () => toast.error(t("Failed to load runs.")),
    }
  );

  // Process API Response
  const apiRows: any[] = useMemo(() => {
    const body = queryResult?.data;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body?.items)) return body.items;
    if (Array.isArray(body?.result)) return body.result;
    return [];
  }, [queryResult]);

  // Combine local and API rows
  const tableData = useMemo(() => {
    const localRowIds = new Set(localRows.map((r) => r.oncologyBatchId));
    const filteredApiRows = apiRows.filter(
      (r) => !localRowIds.has(r.oncologyBatchId)
    );
    return [...localRows, ...filteredApiRows];
  }, [localRows, apiRows]);

  const selectableRows = useMemo(
    () => tableData.filter((row) => !row.rowStatus),
    [tableData]
  );

  const headerCheckboxState = useMemo(() => {
    const selectedOnPage = selectableRows.filter((r) => selected[getRowId(r)]);
    const selectedCount = selectedOnPage.length;
    const total = selectableRows.length;
    return {
      checked: total > 0 && selectedCount === total,
      indeterminate: selectedCount > 0 && selectedCount < total,
    };
  }, [selectableRows, selected]);

  useEffect(() => {
    const tags = (
      Object.keys(FILTER_FIELD_LABELS) as (keyof typeof INITIAL_SEARCH_CRITERIA)[]
    )
      .filter((key) => String(searchCriteria[key] ?? "").trim())
      .map((key) => t(FILTER_FIELD_LABELS[key]));
    setSearchedTags(tags);
  }, [searchCriteria, t]);

  const dismissDraftRow = () => {
    setLocalRows((prev) => prev.filter((row) => !row.rowStatus));
    setIsBtnDisabled(false);
  };

  // Event Handlers
  const handleSearch = () => {
    dismissDraftRow();
    setCurPage(1);
    setTriggerSearch((p) => !p);
  };

  const handleReset = () => {
    dismissDraftRow();
    setSearchCriteria(INITIAL_SEARCH_CRITERIA);
    setSorting(sortById);
    setCurPage(1);
    setReset((p) => !p);
  };

  const handleTagRemoval = (clickedTag: string) => {
    const field = (
      Object.keys(FILTER_FIELD_LABELS) as (keyof typeof INITIAL_SEARCH_CRITERIA)[]
    ).find((key) => t(FILTER_FIELD_LABELS[key]) === clickedTag);
    if (!field) return;
    setSearchCriteria((p) => ({ ...p, [field]: "" }));
    setCurPage(1);
    setTriggerSearch((p) => !p);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const toggleExpand = (id: string | number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const toggleSelectRow = (id: string | number) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const toggleSelectAll = () => {
    const allSelected =
      selectableRows.length > 0 &&
      selectableRows.every((r) => selected[getRowId(r)]);
    const next: Record<string | number, boolean> = {};
    if (!allSelected) {
      selectableRows.forEach((r) => {
        next[getRowId(r)] = true;
      });
    }
    setSelected(next);
  };

  const handleAddNewBatch = () => {
    setIsBtnDisabled(true);
    setLocalRows((prev) => [
      {
        rowStatus: true,
        _clientRowId: `draft-${Date.now()}`,
        ...INITIAL_POST_DATA,
      },
      ...prev,
    ]);
  };

  const updateSearchCriteria = (field: string, value: string) => {
    setSearchCriteria((p) => ({ ...p, [field]: value }));
  };

  const exportBatches = async (selectedIds: number[]) => {
    try {
      const res: any = await ExportBatchesExcel({
        selectedIds,
        queryModel: batchQueryModel,
      });
      const envelope = res?.data;
      const statusCode = envelope?.statusCode ?? envelope?.StatusCode;
      if (statusCode !== 200 && statusCode !== 201) {
        toast.error(
          t(envelope?.message ?? envelope?.Message ?? "Failed to export file.")
        );
        return;
      }

      const payload = envelope?.data ?? envelope?.Data;
      const base64 = payload?.fileContents ?? payload?.FileContents ?? "";
      const fileName =
        payload?.fileDownloadName ??
        payload?.FileDownloadName ??
        "OncologyBatches";

      if (typeof base64 !== "string" || !base64) {
        toast.error(t("Invalid export response."));
        return;
      }
      base64ToExcel(base64, String(fileName));
      toast.success(t("File exported successfully."));
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to export file."));
    }
  };

  const handleExportAll = () => {
    exportBatches([]);
  };

  const handleExportSelected = () => {
    const selectedIds = selectableRows
      .filter((r) => selected[getRowId(r)])
      .map((r) => Number(getRowId(r)))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (selectedIds.length === 0) {
      toast.warning(t("Please select at least one record."));
      return;
    }

    exportBatches(selectedIds);
  };

  return (
    <>
      <OpenRunsToolbar
        t={t}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setCurPage={setCurPage}
        isBtnDisabled={isBtnDisabled}
        loading={loading}
        handleAddNewBatch={handleAddNewBatch}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        handleSearch={handleSearch}
        handleReset={handleReset}
        handleExportAll={handleExportAll}
        handleExportSelected={handleExportSelected}
      />

      <div className="d-flex gap-2 flex-wrap pb-2">
        {searchedTags.map((tag) => (
          <div
            key={tag}
            className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
            onClick={() => handleTagRemoval(tag)}
          >
            <span className="fw-bold">{tag}</span>
            <i className="bi bi-x"></i>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              component={Paper}
              className="shadow-none"
              sx={{
                maxHeight: "calc(100vh - 100px)",
                "&::-webkit-scrollbar": { width: 7 },
                "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--kt-gray-400)",
                  borderRadius: 2,
                },
              }}
            >
              <Table
                aria-label="open runs table"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  {/* Filter Row */}
                  <TableRow className="h-40px">
                    <TableCell className="w-40px" />
                    <TableCell padding="checkbox" className="w-40px" />
                    <TableCell className="w-100px d-none" />
                    <TableCell style={{ minWidth: "160px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-120px w-100 rounded-2 fs-8 h-30px"
                        placeholder={t("Search...")}
                        value={searchCriteria.batchName}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          updateSearchCriteria("batchName", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "120px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                        placeholder={t("Search...")}
                        value={searchCriteria.batchCode}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          updateSearchCriteria("batchCode", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "150px" }}>
                      <SearchDateRangePicker
                        name="openRunsRunDateFilter"
                        value={searchCriteria.runDate}
                        onChange={(e: any) =>
                          updateSearchCriteria("runDate", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "140px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                        placeholder={t("Search...")}
                        value={searchCriteria.plateBarcodeNo}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          updateSearchCriteria("plateBarcodeNo", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell className="min-w-120px" />
                    <TableCell style={{ minWidth: "180px" }}>
                      <Select
                        theme={(theme) => styles(theme)}
                        options={STEP_OPTIONS}
                        value={
                          STEP_OPTIONS.find(
                            (o) => o.value === searchCriteria.stepNumber
                          ) || STEP_OPTIONS[0]
                        }
                        styles={{
                          ...reactSelectSMStyle,
                          control: (provided) => ({
                            ...provided,
                            minWidth: "160px",
                            border: "1px solid #e5e5e5",
                            borderRadius: "6px",
                            "&:hover": { border: "1px solid #e5e5e5" },
                          }),
                        }}
                        menuPortalTarget={document.body}
                        placeholder="--- Select ---"
                        isSearchable={false}
                        onChange={(opt) => {
                          const selected = opt as {
                            value: string;
                            label: string;
                          } | null;
                          updateSearchCriteria(
                            "stepNumber",
                            selected?.value ?? ""
                          );
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Header Row */}
                  <TableRow className="h-30px">
                    <TableCell />
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={headerCheckboxState.checked}
                        indeterminate={headerCheckboxState.indeterminate}
                        onChange={toggleSelectAll}
                        inputProps={{ "aria-label": t("Select all") }}
                        sx={{
                          "&.MuiCheckbox-indeterminate": {
                            color: "var(--bs-primary)",
                          },
                          "&.MuiCheckbox-indeterminate svg path": {
                            color: "var(--bs-primary) !important",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell className="d-none">{t("Actions")}</TableCell>
                    {SORTABLE_COLUMNS.map(({ key, label, width }) => (
                      <TableCell key={key} style={{ minWidth: width }}>
                        <div
                          className="d-flex justify-content-between cursor-pointer"
                          onClick={() => handleSort(key)}
                        >
                          <span style={{ width: "max-content" }}>
                            {t(label)}
                          </span>
                          <div className="d-flex align-items-center mx-4 mr-0">
                            <ArrowUp CustomeClass={sortIcons(key).up} />
                            <ArrowDown CustomeClass={sortIcons(key).down} />
                          </div>
                        </div>
                      </TableCell>
                    ))}
                    <TableCell style={{ minWidth: "120px" }}>
                      {t("Print Label")}
                    </TableCell>
                    <TableCell style={{ minWidth: "180px" }}>
                      {t("Step #")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length ? (
                    tableData.map((row, index) => (
                      <OpenRunsRow
                        key={
                          row.oncologyBatchId
                            ? String(row.oncologyBatchId)
                            : `new-${index}`
                        }
                        row={row}
                        index={index}
                        isOpen={expandedId === getRowId(row)}
                        onToggleExpand={() => toggleExpand(getRowId(row))}
                        isSelected={!!selected[getRowId(row)]}
                        onToggleSelect={() => toggleSelectRow(getRowId(row))}
                        setLocalRows={setLocalRows}
                        setIsBtnDisabled={setIsBtnDisabled}
                        refetch={refetch}
                      />
                    ))
                  ) : (
                    <NoRecord colSpan={9} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>

      <OpenRunsPagination
        t={t}
        pageSize={pageSize}
        curPage={curPage}
        total={total}
        pageNumbers={pageNumbers}
        showPage={showPage}
        prevPage={prevPage}
        nextPage={nextPage}
        totalPages={totalPages}
      />
    </>
  );
};

export default OpenRuns;
